import type { Collab, CollabPreview } from "@cabal/shared/domain/session/collab/types.js";
import { SessionEntityKind } from "@cabal/shared/domain/session/enums.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { ReflectionPreview } from "@cabal/shared/domain/session/reflection/types.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type {
  VersionPreview,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import type { AttachedFileView } from "@cabal/shared/domain/upload/types.js";

export interface ResolvedShortId {
  type: SessionEntityKind;
  entityId: string;
  file?: AttachedFileView;
  product?: ProductSummary;
  version?: VersionSummary | VersionPreview;
  member?: Member;
  collab?: Collab | CollabPreview;
  reflection?: ReflectionPreview;
  userId?: string;
}

interface ShortIdEntry {
  type: SessionEntityKind;
  entityId: string;
}

interface EntityShortId {
  canonical: string;
  formatted: string;
}

export interface SessionEntityLookupSources {
  products: Record<string, ProductSummary>;
  versions: Record<string, VersionSummary>;
  attachedFiles: Record<string, AttachedFileView>;
  members: Record<string, Member>;
  collabs: Record<string, Collab>;
  chatMessages: Record<string, Collab>;
  versionPreviews: Record<string, VersionPreview>;
  collabPreviews: Record<string, CollabPreview>;
  reflectionPreviews: Record<string, ReflectionPreview>;
}

interface ResolveSessionEntityInput {
  type: SessionEntityKind;
  entityId: string;
  sources: SessionEntityLookupSources;
}

type SessionEntityResolver = (
  input: ResolveSessionEntityInput,
) => ResolvedShortId;

const SESSION_ENTITY_RESOLVERS: Record<
  SessionEntityKind,
  SessionEntityResolver
> = {
  [SessionEntityKind.Product]: resolveProductEntity,
  [SessionEntityKind.Version]: resolveVersionEntity,
  [SessionEntityKind.Collab]: resolveCollabEntity,
  [SessionEntityKind.Reflection]: resolveReflectionEntity,
  [SessionEntityKind.Remark]: resolveBaseEntity,
  [SessionEntityKind.File]: resolveFileEntity,
  [SessionEntityKind.Member]: resolveMemberEntity,
  [SessionEntityKind.User]: resolveUserEntity,
};

interface SelectResolvedShortIdInput {
  shortId: string | null | undefined;
  fromShortId: Record<string, ShortIdEntry>;
  sources: SessionEntityLookupSources;
}

export function selectResolvedShortId({
  shortId,
  fromShortId,
  sources,
}: SelectResolvedShortIdInput): ResolvedShortId | null {
  const normalizedShortId = normalizeSessionEntityId(shortId);
  if (!normalizedShortId) return null;

  const entry = fromShortId[normalizedShortId];
  if (!entry) return null;

  return SESSION_ENTITY_RESOLVERS[entry.type]({
    type: entry.type,
    entityId: entry.entityId,
    sources,
  });
}

interface SelectEntityShortIdInput {
  entityId: string | null | undefined;
  toShortId: Record<string, EntityShortId>;
}

export function selectEntityShortId({
  entityId,
  toShortId,
}: SelectEntityShortIdInput): EntityShortId | null {
  const normalizedEntityId = normalizeSessionEntityId(entityId);
  if (!normalizedEntityId) return null;

  return toShortId[normalizedEntityId] ?? null;
}

interface SelectProductByIdInput {
  productId: string | null | undefined;
  products: Record<string, ProductSummary>;
}

/**
 * The product a UUID names. `selectResolvedShortId` starts from a short id; a
 * version already carries its parent's `productId`, so it needs the direct
 * lookup rather than a round trip through the short-id index.
 */
export function selectProductById({
  productId,
  products,
}: SelectProductByIdInput): ProductSummary | null {
  const normalizedProductId = normalizeSessionEntityId(productId);
  if (!normalizedProductId) return null;

  return products[normalizedProductId] ?? null;
}

interface SelectVersionProductIdMapInput {
  products: Record<string, ProductSummary>;
}

export function selectVersionProductIdMap({
  products,
}: SelectVersionProductIdMapInput): Record<string, string> {
  return Object.fromEntries(
    Object.values(products).flatMap((product) =>
      product.versionIds.map((versionId) => [versionId, product.id] as const),
    ),
  );
}

interface SelectVersionHydrationTargetInput {
  versionId: string | null | undefined;
  versions: Record<string, VersionSummary>;
  versionProductIds: Record<string, string>;
}

interface VersionHydrationTarget {
  normalizedVersionId: string | null;
  cachedVersion: VersionSummary | null;
  productId: string | null;
}

export function selectVersionHydrationTarget({
  versionId,
  versions,
  versionProductIds,
}: SelectVersionHydrationTargetInput): VersionHydrationTarget {
  const normalizedVersionId = normalizeSessionEntityId(versionId);
  if (!normalizedVersionId) {
    return {
      normalizedVersionId: null,
      cachedVersion: null,
      productId: null,
    };
  }

  return {
    normalizedVersionId,
    cachedVersion: versions[normalizedVersionId] ?? null,
    productId: versionProductIds[normalizedVersionId] ?? null,
  };
}

interface SelectHydratedVersionInput {
  normalizedVersionId: string;
  hydratedVersions: VersionSummary[];
  versions: Record<string, VersionSummary>;
}

export function selectHydratedVersion({
  normalizedVersionId,
  hydratedVersions,
  versions,
}: SelectHydratedVersionInput): VersionSummary | null {
  return (
    hydratedVersions.find(
      (version) => version.id === normalizedVersionId,
    ) ??
    versions[normalizedVersionId] ??
    null
  );
}

interface SelectLoadedProductCollabsInput {
  productId: string | null | undefined;
  collabsByProduct: Record<string, string[]>;
  collabs: Record<string, Collab>;
  chatMessages: Record<string, Collab>;
}

interface LoadedProductCollabsSelection {
  normalizedProductId: string | null;
  hasLoadedProductCollabs: boolean;
  collabs: Collab[];
}

export function selectLoadedProductCollabs({
  productId,
  collabsByProduct,
  collabs,
  chatMessages,
}: SelectLoadedProductCollabsInput): LoadedProductCollabsSelection {
  const normalizedProductId = normalizeSessionEntityId(productId);
  if (!normalizedProductId) {
    return {
      normalizedProductId: null,
      hasLoadedProductCollabs: false,
      collabs: [],
    };
  }

  const hasLoadedProductCollabs = Object.prototype.hasOwnProperty.call(
    collabsByProduct,
    normalizedProductId,
  );
  if (!hasLoadedProductCollabs) {
    return {
      normalizedProductId,
      hasLoadedProductCollabs: false,
      collabs: [],
    };
  }

  return {
    normalizedProductId,
    hasLoadedProductCollabs: true,
    collabs: (collabsByProduct[normalizedProductId] ?? [])
      .map(
        (collabId) => collabs[collabId] ?? chatMessages[collabId] ?? null,
      )
      .filter((collab): collab is Collab => Boolean(collab)),
  };
}

export function normalizeSessionEntityId(
  entityId: string | null | undefined,
): string | null {
  if (!entityId) return null;

  const trimmedEntityId = entityId.trim();
  return trimmedEntityId.length > 0 ? trimmedEntityId : null;
}

function resolveProductEntity({
  type,
  entityId,
  sources,
}: ResolveSessionEntityInput): ResolvedShortId {
  const product = sources.products[entityId];
  return product ? { type, entityId, product } : { type, entityId };
}

function resolveVersionEntity({
  type,
  entityId,
  sources,
}: ResolveSessionEntityInput): ResolvedShortId {
  const version = sources.versions[entityId] ?? sources.versionPreviews[entityId];
  return version ? { type, entityId, version } : { type, entityId };
}

function resolveMemberEntity({
  type,
  entityId,
  sources,
}: ResolveSessionEntityInput): ResolvedShortId {
  const member = sources.members[entityId];
  return member ? { type, entityId, member } : { type, entityId };
}

function resolveFileEntity({
  type,
  entityId,
  sources,
}: ResolveSessionEntityInput): ResolvedShortId {
  const file = sources.attachedFiles[entityId];
  return file ? { type, entityId, file } : { type, entityId };
}

function resolveCollabEntity({
  type,
  entityId,
  sources,
}: ResolveSessionEntityInput): ResolvedShortId {
  const collab =
    sources.collabs[entityId] ??
    sources.chatMessages[entityId] ??
    sources.collabPreviews[entityId];
  return collab ? { type, entityId, collab } : { type, entityId };
}

function resolveReflectionEntity({
  type,
  entityId,
  sources,
}: ResolveSessionEntityInput): ResolvedShortId {
  const reflection = sources.reflectionPreviews[entityId];
  return reflection ? { type, entityId, reflection } : { type, entityId };
}

function resolveUserEntity({
  type,
  entityId,
}: ResolveSessionEntityInput): ResolvedShortId {
  return { type, entityId, userId: entityId };
}

function resolveBaseEntity({
  type,
  entityId,
}: ResolveSessionEntityInput): ResolvedShortId {
  return { type, entityId };
}
